import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  makeStyles,
  Box,
  CircularProgress,
  Grid,
  Divider,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormHelperText,
  InputAdornment,
  Snackbar,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ContentCopyIcon from "@material-ui/icons/FileCopy";
import EventIcon from "@material-ui/icons/Event";
import PlaceIcon from "@material-ui/icons/Place";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import GetAppIcon from "@material-ui/icons/GetApp";
import ReceiptIcon from "@material-ui/icons/Receipt";
import SearchIcon from "@material-ui/icons/Search";
import { Link, useLocation, useParams } from "react-router-dom";
import AiSensyLogo from "../../static/AiSensyLogo.svg";
import { countries } from "./countryData";
import eventApiService from "../../services/eventApi";
import posthog from "../../config/posthog";
import { CalendarIcon } from "lucide-react";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    padding: 0,
    margin: 0,
    backgroundImage: `
      linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(250, 250, 252, 0.95)), 
      url('https://images.unsplash.com/photo-1546412414-e1885e51cec5?q=80&w=1974')
    `,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    position: "relative",
    color: "#111827",
    [theme.breakpoints.down("xs")]: {
      backgroundAttachment: "scroll",
    },
  },
  container: {
    position: "relative",
    zIndex: 1,
    padding: theme.spacing(3, 2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2, 1),
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  contentWrapper: {
    width: "100%",
    maxWidth: 700,
  },
  header: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  logo: {
    height: 36,
    marginRight: theme.spacing(1),
  },
  evolveTitle: {
    fontSize: "clamp(2rem, 6vw, 3.8rem)",
    fontWeight: 800,
    lineHeight: 1,
    marginBottom: theme.spacing(0.5),
    backgroundImage: "linear-gradient(to right, #111827, #04cf66)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    textTransform: "uppercase",
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    textAlign: "center",
    letterSpacing: "-0.01em",
    [theme.breakpoints.down("xs")]: {
      fontSize: "2rem",
    },
  },
  subtitle: {
    fontSize: "clamp(0.7rem, 1.5vw, 0.75rem)",
    letterSpacing: "0.1em",
    color: "#6B7280",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    textAlign: "center",
    fontWeight: 600,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.65rem",
      marginBottom: theme.spacing(0.5),
    },
  },
  thankYouSection: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2, 3),
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.03)",
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "4px",
      background: (props) =>
        `linear-gradient(to right, ${props.primaryColor}, ${props.secondaryColor})`,
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(2),
    },
  },
  successIcon: {
    width: 60,
    height: 60,
    backgroundColor: (props) => `${props.secondaryColor}40`,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    marginBottom: theme.spacing(2),
    "& svg": {
      color: (props) => props.primaryColor,
      fontSize: 36,
    },
  },
  congratsText: {
    fontSize: "clamp(1.4rem, 4vw, 1.7rem)",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: "#111827",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.3rem",
      marginBottom: theme.spacing(0.5),
    },
  },
  highlightText: {
    color: (props) => props.primaryColor,
    fontWeight: 700,
  },
  orderSummarySection: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(2),
    },
  },
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
    color: "#111827",
    position: "relative",
    paddingLeft: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "4px",
      height: "70%",
      background: (props) =>
        `linear-gradient(to bottom, ${props.primaryColor}, ${props.secondaryColor})`,
      borderRadius: "2px",
    },
  },
  ticketCard: {
    backgroundColor: "white",
    borderRadius: "10px",
    overflow: "hidden",
    position: "relative",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.04)",
  },
  ticketHeader: {
    padding: theme.spacing(1, 1.5),
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    backgroundColor: (props) => `${props.secondaryColor}20`,
    display: "flex",
    alignItems: "center",
  },
  ticketHeaderIcon: {
    marginRight: theme.spacing(1),
    color: (props) => props.primaryColor,
    fontSize: "1.2rem",
  },
  ticketTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#374151",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
    },
  },
  ticketContent: {
    padding: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  ticketDetail: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.3),
    padding: theme.spacing(0.2, 0),
    fontSize: "0.85rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
      marginBottom: theme.spacing(0.2),
    },
  },
  ticketType: {
    color: "#4B5563",
    fontWeight: 500,
  },
  ticketPrice: {
    color: (props) => props.primaryColor,
    fontWeight: 600,
  },
  ticketTotal: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: (props) => `${props.secondaryColor}20`,
    borderRadius: "6px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  totalText: {
    color: "#374151",
    fontWeight: 600,
    fontSize: "0.85rem",
  },
  totalAmount: {
    color: (props) => props.primaryColor,
    fontWeight: 700,
    fontSize: "1.1rem",
  },
  eventDetails: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1),
    backgroundColor: "rgba(0, 0, 0, 0.02)",
    borderRadius: "6px",
  },
  eventDetailItem: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.85rem",
    color: "#4B5563",
  },
  eventDetailIcon: {
    color: "#6B7280",
    fontSize: "1rem",
    marginRight: theme.spacing(1),
  },
  formSection: {
    marginTop: theme.spacing(2),
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      marginTop: theme.spacing(1.5),
    },
  },
  formTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    marginBottom: theme.spacing(2),
    color: "#111827",
    position: "relative",
    paddingLeft: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "4px",
      height: "70%",
      background: "linear-gradient(to bottom, #04cf66, #28E0B9)",
      borderRadius: "2px",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "1rem",
      marginBottom: theme.spacing(1.5),
    },
  },
  attendeeForm: {
    backgroundColor: "white",
    borderRadius: "10px",
    marginBottom: theme.spacing(2),
    overflow: "hidden",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.04)",
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  formHeader: {
    backgroundColor: (props) => `${props.secondaryColor}20`,
    padding: theme.spacing(1.2, 2),
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.8, 1.2),
    },
  },
  formContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1),
    },
  },
  copyButton: {
    backgroundColor: (props) => `${props.secondaryColor}30`,
    color: (props) => props.primaryColor,
    border: (props) => `1px solid ${props.primaryColor}40`,
    padding: theme.spacing(0.1, 0.8),
    fontSize: "0.65rem",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.1, 0.6),
      fontSize: "0.6rem",
    },
    "&:hover": {
      backgroundColor: (props) => `${props.secondaryColor}50`,
    },
    textTransform: "none",
    minWidth: "auto",
    borderRadius: "4px",
    fontWeight: 500,
    height: "24px",
    [theme.breakpoints.down("xs")]: {
      height: "22px",
    },
  },
  copyIcon: {
    fontSize: "0.75rem",
    marginRight: theme.spacing(0.3),
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.7rem",
      marginRight: theme.spacing(0.2),
    },
  },
  submitButton: {
    background: "linear-gradient(to right, #04cf66, #28E0B9)",
    color: "white",
    padding: theme.spacing(1.2, 0),
    fontWeight: 600,
    fontSize: "0.95rem",
    marginTop: theme.spacing(1),
    "&:hover": {
      background: "linear-gradient(to right, #03b85a, #25C9A7)",
    },
    width: "100%",
    borderRadius: "8px",
    textTransform: "none",
    boxShadow: (props) => `0 4px 12px ${props.primaryColor}30`,
    transition: "all 0.2s ease",
    "&:active": {
      transform: "scale(0.98)",
    },
  },
  divider: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    margin: theme.spacing(0.7, 0),
  },
  formSubmitArea: {
    marginTop: theme.spacing(3),
    backgroundColor: "white",
    borderRadius: "12px",
    padding: theme.spacing(2),
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.04)",
    maxWidth: 700,
    margin: "0 auto",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
      marginTop: theme.spacing(2),
    },
  },
  successMessage: {
    backgroundColor: (props) => `${props.secondaryColor}20`,
    color: (props) => props.primaryColor,
    padding: theme.spacing(2),
    borderRadius: "10px",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    "& .MuiSvgIcon-root": {
      marginRight: theme.spacing(1),
    },
    border: (props) => `1px solid ${props.secondaryColor}50`,
  },
  bookAgainButton: {
    background: "linear-gradient(to right, #04cf66, #28E0B9)",
    color: "white",
    padding: theme.spacing(0.8, 3),
    borderRadius: "8px",
    fontWeight: 600,
    marginTop: theme.spacing(2),
    "&:hover": {
      background: "linear-gradient(to right, #03b85a, #25C9A7)",
    },
    textTransform: "none",
    fontSize: "0.9rem",
    boxShadow: (props) => `0 4px 12px ${props.primaryColor}30`,
  },
  attendeeTitle: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#374151",
    display: "flex",
    alignItems: "center",
  },
  attendeeCount: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    borderRadius: "50%",
    backgroundColor: (props) => props.primaryColor,
    color: "white",
    fontSize: "0.7rem",
    fontWeight: 700,
    marginRight: theme.spacing(1),
  },
  messageText: {
    fontSize: "clamp(0.85rem, 2vw, 0.95rem)",
    color: "#4B5563",
    lineHeight: 1.6,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.85rem",
      lineHeight: 1.4,
    },
  },
  headerTitle: {
    fontSize: "1.1rem",
    color: "#111827",
    margin: theme.spacing(1, 0, 1.5),
    fontWeight: 700,
    position: "relative",
    paddingLeft: theme.spacing(1),
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "4px",
      height: "70%",
      background: "linear-gradient(to bottom, #04cf66, #28E0B9)",
      borderRadius: "2px",
    },
  },
  secureNotice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.75rem",
    color: "#6B7280",
    marginTop: theme.spacing(3),
    "& svg": {
      fontSize: "0.85rem",
      marginRight: theme.spacing(0.5),
    },
  },
  eventTimeLocation: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(0.8),
      marginBottom: theme.spacing(2),
    },
  },
  eventDetailPill: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.85rem",
    color: "#374151",
    padding: theme.spacing(0.7, 1.5),
    backgroundColor: (props) => `${props.secondaryColor}40`,
    borderRadius: "20px",
    fontWeight: 500,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    border: (props) => `1px solid ${props.secondaryColor}60`,
    "& svg": {
      marginRight: theme.spacing(0.7),
      fontSize: "1rem",
      color: (props) => props.primaryColor,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.75rem",
      padding: theme.spacing(0.5, 1.2),
      width: "90%",
      justifyContent: "center",
    },
  },
  eventBadges: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.8),
    marginBottom: theme.spacing(2),
    justifyContent: "center",
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(0.5),
      marginBottom: theme.spacing(1.5),
    },
  },
  dubaiChip: {
    backgroundColor: "#FFF8E1",
    color: "#FF9800",
    fontWeight: 500,
    fontSize: "0.7rem",
    border: "1px solid rgba(255, 152, 0, 0.2)",
    height: "24px",
  },
  whatsappChip: {
    backgroundColor: "#E8F5E9",
    color: "#25D366",
    fontWeight: 500,
    fontSize: "0.7rem",
    border: "1px solid rgba(37, 211, 102, 0.2)",
    height: "24px",
  },
  aiChip: {
    backgroundColor: "#E3F2FD",
    color: "#2196F3",
    fontWeight: 500,
    fontSize: "0.7rem",
    border: "1px solid rgba(33, 150, 243, 0.2)",
    height: "24px",
  },
  businessChip: {
    backgroundColor: "#FFF8E1",
    color: "#FF9800",
    fontWeight: 500,
    fontSize: "0.7rem",
    border: "1px solid rgba(255, 152, 0, 0.2)",
    height: "24px",
  },
  customIcon: {
    width: 16,
    height: 16,
    marginRight: theme.spacing(0.5),
  },
  summaryAccordion: {
    borderRadius: "10px",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
    "&::before": {
      display: "none",
    },
    "& .MuiAccordionSummary-root": {
      padding: theme.spacing(0.5, 1),
      minHeight: "auto",
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(0.3, 0.8),
      },
    },
    "& .MuiAccordionSummary-content": {
      margin: "4px 0px",
    },
    "& .MuiAccordionDetails-root": {
      padding: theme.spacing(1),
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(0.8),
      },
    },
  },
  expandIcon: {
    color: (props) => props.primaryColor,
  },
  dubaiChipIcon: {
    color: "#FF9800",
  },
  whatsappChipIcon: {
    color: "#25D366",
  },
  aiChipIcon: {
    color: "#2196F3",
  },
  businessChipIcon: {
    color: "#FF9800",
  },
  discountInfo: {
    color: "#FF9800",
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: "rgba(255, 152, 0, 0.04)",
    borderRadius: "6px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    [theme.breakpoints.down("xs")]: {
      minHeight: "50vh",
    },
  },
  loadingText: {
    marginTop: theme.spacing(2),
    color: "#6B7280",
    fontSize: "0.9rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
    },
  },
  counterContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "20px 0",
  },
  counterButton: {
    minWidth: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: (props) => props.primaryColor,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "none",
    fontSize: "20px",
    "&:hover": {
      backgroundColor: "#03b85a",
    },
  },
  counterDisplay: {
    margin: "0 20px",
    fontSize: "24px",
    fontWeight: "bold",
    minWidth: "50px",
    textAlign: "center",
  },

  // Enhanced order breakdown styles - Mobile First
  orderBreakdown: {
    backgroundColor: "white",
    borderRadius: "10px",
    overflow: "hidden",
    // Mobile-first: start with mobile styles
    [theme.breakpoints.up("sm")]: {
      borderRadius: "12px",
    },
  },
  orderHeader: {
    backgroundColor: (props) => `${props.secondaryColor}20`,
    padding: theme.spacing(2, 2.5),
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5, 2),
    },
  },
  orderTitle: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginRight: theme.spacing(1),
      color: (props) => props.primaryColor,
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "1rem",
    },
  },
  orderContent: {
    padding: theme.spacing(0),
  },
  orderSection: {
    // Mobile-first: start with mobile styles - Much more compact
    padding: theme.spacing(1.5, 1.5),
    "&:not(:last-child)": {
      borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
    },
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 2.5),
    },
  },
  orderSectionTitle: {
    // Mobile-first: start with mobile styles - Reduced margins
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "#374151",
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.85rem",
      marginBottom: theme.spacing(1.2),
    },
  },
  ticketItem: {
    // Mobile-first: Price alongside ticket name
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.2),
    padding: theme.spacing(1.2, 1),
    backgroundColor: (props) => `${props.secondaryColor}15`,
    borderRadius: "8px",
    border: (props) => `1px solid ${props.secondaryColor}40`,
    [theme.breakpoints.up("sm")]: {
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5, 1.2),
      marginBottom: theme.spacing(1),
    },
  },
  ticketInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    gap: theme.spacing(0.5),
  },
  ticketName: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(0.6),
    lineHeight: 1.2,
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.85rem",
    },
  },
  ticketBadge: {
    padding: theme.spacing(0.1, 0.5),
    borderRadius: "8px",
    fontSize: "0.6rem",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.65rem",
      padding: theme.spacing(0.15, 0.6),
    },
  },
  earlyBirdBadge: {
    backgroundColor: "#FEF3C7",
    color: "#D97706",
    border: "1px solid #FDE68A",
  },
  groupBadge: {
    backgroundColor: "#DBEAFE",
    color: "#1D4ED8",
    border: "1px solid #BFDBFE",
  },
  ticketQuantity: {
    fontSize: "0.75rem",
    color: "#6B7280",
    fontWeight: 500,
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.8rem",
    },
  },
  ticketPrice: {
    // Mobile-first: Price alongside ticket info
    textAlign: "right",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: theme.spacing(0.2),
    minWidth: "80px",
    [theme.breakpoints.up("sm")]: {
      minWidth: "100px",
    },
  },
  ticketUnitPrice: {
    fontSize: "0.7rem",
    color: "#6B7280",
    lineHeight: 1.1,
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.75rem",
    },
  },
  ticketTotalPrice: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: (props) => props.primaryColor,
    lineHeight: 1.1,
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.9rem",
    },
  },
  summaryRow: {
    // Mobile-first: Much more compact with smaller fonts
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(0.8, 0),
    fontSize: "0.8rem",
    minHeight: "26px",
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.85rem",
      padding: theme.spacing(0.7, 0),
      minHeight: "28px",
    },
  },
  summaryLabel: {
    color: "#6B7280",
    fontWeight: 500,
  },
  summaryValue: {
    color: "#111827",
    fontWeight: 600,
  },
  subtotalRow: {
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
    paddingTop: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  discountRow: {
    "& $summaryValue": {
      color: "#DC2626",
    },
  },
  gstRow: {
    "& $summaryLabel": {
      display: "flex",
      alignItems: "center",
      fontSize: "0.85rem",
    },
    "& $summaryValue": {
      color: "#6B7280",
    },
  },
  finalAmountRow: {
    borderTop: (props) => `1.5px solid ${props.primaryColor}50`,
    backgroundColor: (props) => `${props.secondaryColor}20`,
    borderRadius: "6px",
    padding: theme.spacing(1.2, 1),
    margin: theme.spacing(1, 0, 0, 0),
    "& .MuiTypography-root": {
      fontWeight: "700 !important",
    },
    "& $summaryLabel": {
      fontSize: "0.85rem",
      fontWeight: 700,
      color: "#111827",
    },
    "& $summaryValue": {
      fontSize: "1rem",
      fontWeight: 700,
      color: (props) => props.primaryColor,
    },
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(1.2, 0),
      margin: theme.spacing(1.2, 0, 0, 0),
      "& $summaryLabel": {
        fontSize: "0.9rem",
      },
      "& $summaryValue": {
        fontSize: "1.1rem",
      },
    },
  },
  gstDetails: {
    fontSize: "0.75rem",
    color: "#6B7280",
    marginLeft: theme.spacing(0.5),
    fontFamily: "monospace",
  },
  invoiceSection: {
    backgroundColor: "rgba(59, 130, 246, 0.04)",
    borderTop: "1px solid rgba(59, 130, 246, 0.1)",
  },
  invoiceHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(1),
    },
  },
  invoiceInfo: {
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginRight: theme.spacing(1),
      color: "#3B82F6",
    },
  },
  invoiceNumber: {
    fontFamily: "monospace",
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#1E40AF",
  },
  invoiceActions: {
    display: "flex",
    gap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      "& > *": {
        flex: 1,
      },
    },
  },
  eventTitle: {
    fontSize: "28px",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    letterSpacing: "0.2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      fontSize: "24px",
      marginBottom: theme.spacing(1),
    },
  },
  invoiceButton: {
    background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
    color: "white",
    padding: theme.spacing(0.8, 1.5),
    fontSize: "0.8rem",
    fontWeight: 500,
    textTransform: "none",
    borderRadius: "6px",
    minWidth: "auto",
    "&:hover": {
      background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
    },
    "& svg": {
      marginRight: theme.spacing(0.5),
      fontSize: "1rem",
    },
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 1.2),
      fontSize: "0.75rem",
    },
  },
  paymentStatus: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.5, 1.2),
    borderRadius: "20px",
    fontSize: "0.8rem",
    fontWeight: 600,
    backgroundColor: (props) => `${props.secondaryColor}40`,
    color: (props) => props.primaryColor,
    border: (props) => `1px solid ${props.primaryColor}40`,
    "& svg": {
      marginRight: theme.spacing(0.5),
      fontSize: "1rem",
    },
  },
  dividerWithText: {
    position: "relative",
    margin: theme.spacing(2, 0),
    height: "1px",
    backgroundColor: "rgba(0, 0, 0, 0.08)",
    "&::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      padding: theme.spacing(0, 2),
      backgroundColor: "white",
      fontSize: "0.75rem",
      color: "#9CA3AF",
      fontWeight: 500,
    },
  },
  orderAccordion: {
    marginBottom: theme.spacing(2),
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.06)",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: theme.spacing(0, 0, 2, 0),
    },
  },
  orderAccordionSummary: {
    borderRadius: "12px",
    padding: theme.spacing(0, 1),
    minHeight: "auto",
    backgroundColor: "#ffffff",
    "& .MuiAccordionSummary-content": {
      margin: 0,
      alignItems: "center",
    },
    "&.Mui-expanded": {
      minHeight: "auto",
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      borderBottom: "1px solid rgba(0,0,0,0.06)",
    },
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  orderAccordionDetails: {
    padding: 0,
    backgroundColor: "#ffffff",
    borderBottomLeftRadius: "16px",
    borderBottomRightRadius: "16px",
    "&.MuiAccordionDetails-root": {
      display: "block",
    },
  },
  accordionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: theme.spacing(1),
  },
  accordionIcon: {
    color: (props) => props.primaryColor,
    fontSize: "1rem",
  },
  accordionTitle: {
    fontWeight: 600,
    fontSize: "13px",
    color: "#1f2937",
    flex: 1,
    marginLeft: theme.spacing(0.6),
    [theme.breakpoints.up("sm")]: {
      fontSize: "14px",
    },
  },
  paymentStatusSmall: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.3, 0.8),
    borderRadius: "16px",
    fontSize: "0.7rem",
    fontWeight: 600,
    backgroundColor: (props) => `${props.secondaryColor}40`,
    color: (props) => props.primaryColor,
    border: (props) => `1px solid ${props.primaryColor}30`,
    "& svg": {
      marginRight: theme.spacing(0.4),
      fontSize: "0.8rem",
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.75rem",
      padding: theme.spacing(0.4, 0.9),
    },
  },
  greenText: {
    color: "#FF8C00",
  },
  // Form styling to match PaymentPage
  formFieldLabel: {
    marginBottom: "4px",
    fontWeight: 500,
    fontSize: "12px",
    color: "#374151",
    display: "block",
  },
  formFieldInput: {
    padding: 0,
    marginBottom: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      padding: "1px",
      borderRadius: "0px",
      backgroundColor: "white",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.08)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0, 0, 0, 0.15)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(0, 0, 0, 0.2)",
        borderWidth: "1px",
      },
      "&.Mui-error fieldset": {
        borderColor: "#f44336",
      },
    },
    "&.MuiTextField-root": {
      padding: "0px 4px",
      backgroundColor: "#FFFFFF",
      borderRadius: "4px",
      border: "0.5px solid #e0e0e0",
    },
    "& .MuiOutlinedInput-input": {
      padding: "4px 8px",
      fontSize: "14px",
      "&::placeholder": {
        color: "#9CA3AF",
        opacity: 1,
      },
    },
    "& .MuiInputLabel-root": {
      display: "none",
    },
    "& .MuiFormHelperText-root": {
      marginTop: "2px",
      fontSize: "11px",
      marginLeft: "0px",
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(0.8),
    },
  },
  // Country code input styling
  countryCodeInput: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f9f9f9",
    borderRight: "1px solid rgba(0, 0, 0, 0.08)",
    padding: "0 8px",
    minWidth: "80px",
    height: "100%",
    "&:hover": {
      backgroundColor: "#f0f9f1",
    },
  },
  phoneInputContainer: {
    display: "flex",
    alignItems: "center",
    "& .MuiOutlinedInput-root": {
      paddingLeft: 0,
    },
  },
  mobileMenuPaper: {
    maxHeight: "300px",
    "& .MuiMenuItem-root": {
      minHeight: "40px",
      padding: "8px 16px",
      fontSize: "14px",
    },
  },
  // Simple select styling
  simpleSelect: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    border: "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: "0px",
    backgroundColor: "white",
    color: "#111827",
    outline: "none",
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: "right 12px center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "16px",
    paddingRight: "40px",
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.15)",
    },
    "&:focus": {
      borderColor: "rgba(0, 0, 0, 0.2)",
      boxShadow: "none",
    },
    "&.error": {
      borderColor: "#f44336",
    },
  },
  selectErrorText: {
    color: "#f44336",
    fontSize: "11px",
    marginTop: "2px",
    marginLeft: "0px",
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
  },
  eventDate: {
    display: "flex",
    alignItems: "center",
    // marginBottom: theme.spacing(3),
    color: "#666",
    // backgroundColor: "#f0f9f1",
    padding: theme.spacing(1, 0),
    borderRadius: "20px",
    display: "inline-flex",
    fontSize: "14px",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      // marginBottom: theme.spacing(4),
      width: "100%",
    },
  },
  eventDateText: {
    fontWeight: 600,
    letterSpacing: "0.3px",
  },
}));

// Add SVG icons similar to PaymentPage
const MapPinIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
    <circle cx="12" cy="10" r="3"></circle>
  </svg>
);

const MessageSquareIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const GlobeIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" x2="22" y1="12" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const BriefcaseIcon = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect width="20" height="14" x="2" y="7" rx="2" ry="2"></rect>
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
  </svg>
);

// Currency formatter helper
const formatCurrency = (amount, currency) => {
  if (currency === "INR") {
    return `₹${amount?.toLocaleString("en-IN") || 0}`;
  }
  return `$${amount?.toLocaleString("en-US") || 0}`;
};

// Enhanced Order Breakdown Component
const OrderBreakdown = ({ customerData, eventData, classes }) => {
  if (!customerData) return null;

  const {
    tickets,
    subtotal,
    discount,
    gstDetails,
    finalAmount,
    currency,
    invoiceDetails,
  } = customerData;

  return (
    <Box className={classes.orderBreakdown}>
      <Box className={classes.orderContent}>
        {/* Tickets Section */}
        <Box className={classes.orderSection}>
          <Typography className={classes.orderSectionTitle}>
            Tickets Purchased
          </Typography>

          {tickets?.map((ticket, index) => (
            <Box key={index} className={classes.ticketItem}>
              <Box className={classes.ticketInfo}>
                <Box className={classes.ticketName}>
                  {ticket.title}
                  {ticket.isEarlyBird && (
                    <span
                      className={`${classes.ticketBadge} ${classes.earlyBirdBadge}`}
                    >
                      Early Bird
                    </span>
                  )}
                  {ticket.isGroup && (
                    <span
                      className={`${classes.ticketBadge} ${classes.groupBadge}`}
                    >
                      Group
                    </span>
                  )}
                </Box>
                <Typography className={classes.ticketQuantity}>
                  Quantity: {ticket.quantity}
                </Typography>
              </Box>
              <Box className={classes.ticketPrice}>
                {/* <Typography className={classes.ticketUnitPrice}>
                  {formatCurrency(ticket.price, currency)} each
                </Typography> */}
                <Typography className={classes.ticketTotalPrice}>
                  {formatCurrency(ticket.price * ticket.quantity, currency)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Price Breakdown Section */}
        <Box className={classes.orderSection}>
          <Typography className={classes.orderSectionTitle}>
            Price Breakdown
          </Typography>

          {/* Subtotal */}
          <Box className={`${classes.summaryRow} ${classes.subtotalRow}`}>
            <Typography className={classes.summaryLabel}>Subtotal</Typography>
            <Typography className={classes.summaryValue}>
              {formatCurrency(subtotal, currency)}
            </Typography>
          </Box>

          {/* Discount */}
          {discount > 0 && (
            <Box className={`${classes.summaryRow} ${classes.discountRow}`}>
              <Typography className={classes.summaryLabel}>
                Discount Applied
              </Typography>
              <Typography className={classes.summaryValue}>
                -{formatCurrency(discount, currency)}
              </Typography>
            </Box>
          )}

          {/* GST */}
          {gstDetails?.isApplicable && !customerData?.priceIncludeGST && (
            <Box className={`${classes.summaryRow} ${classes.gstRow}`}>
              <Typography className={classes.summaryLabel}>
                GST ({gstDetails.rate}%)
                {/* {gstDetails.gstNumber && (
                  <span className={classes.gstDetails}>
                    {gstDetails.gstNumber}
                  </span>
                )} */}
              </Typography>
              <Typography className={classes.summaryValue}>
                {formatCurrency(gstDetails.amount, currency)}
              </Typography>
            </Box>
          )}

          {/* Final Amount */}
          <Box className={`${classes.summaryRow} ${classes.finalAmountRow}`}>
            <Typography className={classes.summaryLabel}>
              Total Paid
              {customerData?.priceIncludeGST && gstDetails?.isApplicable && (
                <Typography
                  component="span"
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 400,
                    color: "#6B7280",
                    marginLeft: "4px",
                  }}
                >
                  (incl GST)
                </Typography>
              )}
            </Typography>
            <Typography className={classes.summaryValue}>
              {formatCurrency(finalAmount, currency)}
            </Typography>
          </Box>
        </Box>

        {/* Invoice Section */}
        {invoiceDetails?.generateInvoice && invoiceDetails?.pdfLink && (
          <Box className={`${classes.orderSection} ${classes.invoiceSection}`}>
            <Typography className={classes.orderSectionTitle}>
              Tax Invoice
            </Typography>

            <Box className={classes.invoiceHeader}>
              <Box className={classes.invoiceInfo}>
                <ReceiptIcon />
                <Box>
                  <Typography className={classes.invoiceNumber}>
                    Invoice #{invoiceDetails.invoiceNumber}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Generated on{" "}
                    {new Date(
                      invoiceDetails.invoiceGeneratedAt
                    ).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </Typography>
                </Box>
              </Box>

              <Box className={classes.invoiceActions}>
                {/* {invoiceDetails.invoiceLink && (
                  <Button
                    href={invoiceDetails.invoiceLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.invoiceButton}
                    size="small"
                  >
                    <ReceiptIcon />
                    View Invoice
                  </Button>
                )} */}
                {invoiceDetails.pdfLink && (
                  <Button
                    href={invoiceDetails.pdfLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.invoiceButton}
                    size="small"
                  >
                    <GetAppIcon />
                    Download PDF
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

function ThankYouPage() {
  const theme = useTheme();
  const location = useLocation();
  const { eventSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [eventConfig, setEventConfig] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);

  // Dynamic colors from eventConfig
  const primaryColor = eventConfig?.primaryColor || "#FF8C00";
  const secondaryColor = eventConfig?.secondaryColor || "#FFEDD2";

  const classes = useStyles({ primaryColor, secondaryColor });

  // For form errors
  const [formErrors, setFormErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState("");
  const [preventExitConfirmation, setPreventExitConfirmation] = useState(false);

  // Add industry options
  const industries = [
    "Real Estate",
    "Technology",
    "Finance",
    "Healthcare",
    "Education",
    "E-commerce",
    "Manufacturing",
    "Professional Services",
    "Marketing",
    "Media",
    "Telecommunications",
    "Travel",
    "Government & Public Sector",
    "Non-Profit",
    "Others",
  ];

  // Country search and selection states
  const [countrySearch, setCountrySearch] = useState("");
  const [selectedCountries, setSelectedCountries] = useState({}); // Store per attendee

  // Filter countries based on search
  const getFilteredCountries = () => {
    if (!countrySearch.trim()) return countries;
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
        country.dial_code.includes(countrySearch)
    );
  };

  // Initialize country selection for an attendee
  const getSelectedCountry = (index) => {
    return (
      selectedCountries[index] || {
        code: "IN",
        dial_code: "+91",
        name: "India",
        flag: "🇮🇳",
      }
    );
  };

  // Update country selection for specific attendee
  const updateSelectedCountry = (index, country) => {
    setSelectedCountries((prev) => ({
      ...prev,
      [index]: country,
    }));
  };

  // Add organization size options
  const organizationSizes = [
    "1-10 employees",
    "10-20 employees",
    "20-50 employees",
    "50-200 employees",
    "200+ employees",
  ];

  // Function to fetch customer details from new API
  const fetchCustomerDetails = async (customerId, orderId) => {
    setLoading(true);
    setError(null);
    setErrorType(null);

    try {
      const response = await eventApiService.getCustomerDetails(
        eventSlug,
        customerId,
        orderId
      );

      if (response.success && response.data) {
        const data = response.data;
        setCustomerData(data);
        setEventData({
          eventName: data.eventName,
          location: data.location,
          currency: data.currency,
        });

        // Check journey state and attendees
        if (data.attendees && data.attendees.length > 0) {
          // Attendees already added - show completion
          setSubmitSuccess(true);
          setAttendees(data.attendees);
        } else if (data.needsAttendeeDetails) {
          // Need to collect attendee details
          initializeAttendeesForms(data);
        } else {
          // Single attendee - auto-processed, show success
          setSubmitSuccess(true);
        }
      }
    } catch (err) {
      setEventData({
        eventName: "EVOLVE'25 Bangalore",
        location: "Bangalore, India",
        currency: "INR",
        dateTime: "September 24th, 2025 | 9:00 AM - 7:00 PM",
      });

      if (err.data && err.data.type) {
        setErrorType(err.data.type);
        setError(err.message || "An error occurred");
      } else {
        setErrorType("UNKNOWN_ERROR");
        setError("Unable to load registration details. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Initialize attendee forms based on customer data
  const initializeAttendeesForms = (customerData) => {
    const initialAttendees = [];
    let attendeeIndex = 0;

    // Create attendee forms based on tickets
    customerData.tickets.forEach((ticket) => {
      for (let i = 0; i < ticket.quantity; i++) {
        const isFirstAttendee = attendeeIndex === 0;

        initialAttendees.push({
          ticketType: ticket.title,
          ticketId: ticket.id,
          fullName: isFirstAttendee ? customerData.fullName || "" : "",
          email: isFirstAttendee ? customerData.email || "" : "",
          countryCode: isFirstAttendee
            ? customerData.countryCallingCode || "+91"
            : "+91",
          phone: isFirstAttendee ? customerData.phone || "" : "",
          organization: isFirstAttendee
            ? customerData.organizationName || ""
            : "",
          organizationSize: isFirstAttendee
            ? customerData.organizationSize || ""
            : "",
          industry: isFirstAttendee ? customerData.industry || "" : "",
          designation: "",
          website: "",
        });

        attendeeIndex++;
      }
    });

    setAttendees(initialAttendees);
  };

  // Calculate total amount from customer data
  const calculateTotal = () => {
    if (!customerData) return 0;
    return customerData.finalAmount || 0;
  };

  // Update handleAttendeeChange to include new fields
  const handleAttendeeChange = (index, field, value) => {
    setAttendees((prevAttendees) => {
      const newAttendees = [...prevAttendees];
      if (!newAttendees[index]) {
        newAttendees[index] = {};
      }
      newAttendees[index][field] = value;
      return newAttendees;
    });

    // Clear any related error
    if (formErrors[`attendee-${index}-${field}`]) {
      setFormErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[`attendee-${index}-${field}`];
        return newErrors;
      });
    }
  };

  // Copy details from the first attendee (exclude phone and email for uniqueness)
  const copyFromFirstAttendee = (index) => {
    if (attendees.length > 0 && index > 0) {
      setAttendees((prevAttendees) => {
        const newAttendees = [...prevAttendees];
        const firstAttendee = prevAttendees[0];

        // Copy selected fields but keep phone and email unique
        newAttendees[index] = {
          ...newAttendees[index],
          // Keep existing unique fields
          fullName: newAttendees[index].fullName || "",
          email: newAttendees[index].email || "",
          phone: newAttendees[index].phone || "",
          // Copy these fields from first attendee
          countryCode: firstAttendee.countryCode,
          organization: firstAttendee.organization,
          website: firstAttendee.website,
          industry: firstAttendee.industry,
          organizationSize: firstAttendee.organizationSize,
          designation: firstAttendee.designation,
          // Keep the ticket information
          ticketType: newAttendees[index].ticketType,
          ticketId: newAttendees[index].ticketId,
        };

        return newAttendees;
      });

      // Also copy the country selection
      if (selectedCountries[0]) {
        updateSelectedCountry(index, selectedCountries[0]);
      }

      // Clear any errors related to copied fields (but keep phone/email errors)
      setFormErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        const fieldsToClear = [
          "countryCode",
          "organization",
          "website",
          "industry",
          "organizationSize",
          "designation",
        ];
        fieldsToClear.forEach((field) => {
          const errorKey = `attendee-${index}-${field}`;
          if (newErrors[errorKey]) {
            delete newErrors[errorKey];
          }
        });
        return newErrors;
      });
    }
  };

  // Update validateForm to include new fields
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    let firstErrorAttendee = -1;
    let firstErrorField = "";

    attendees.forEach((attendee, index) => {
      // Existing validations...
      if (!attendee.fullName || attendee.fullName.trim() === "") {
        newErrors[`attendee-${index}-fullName`] = "Full name is required";
        isValid = false;
        if (firstErrorAttendee === -1) {
          firstErrorAttendee = index;
          firstErrorField = "fullName";
        }
      }

      if (!attendee.email || attendee.email.trim() === "") {
        newErrors[`attendee-${index}-email`] = "Email is required";
        isValid = false;
        if (firstErrorAttendee === -1) {
          firstErrorAttendee = index;
          firstErrorField = "email";
        }
      } else if (!/\S+@\S+\.\S+/.test(attendee.email)) {
        newErrors[`attendee-${index}-email`] = "Invalid email format";
        isValid = false;
        if (firstErrorAttendee === -1) {
          firstErrorAttendee = index;
          firstErrorField = "email";
        }
      }

      if (!attendee.phone || attendee.phone.trim() === "") {
        newErrors[`attendee-${index}-phone`] = "Phone number is required";
        isValid = false;
        if (firstErrorAttendee === -1) {
          firstErrorAttendee = index;
          firstErrorField = "phone";
        }
      }

      if (!attendee.organization || attendee.organization.trim() === "") {
        newErrors[`attendee-${index}-organization`] =
          "Organization is required";
        isValid = false;
        if (firstErrorAttendee === -1) {
          firstErrorAttendee = index;
          firstErrorField = "organization";
        }
      }

      // Validate additional required fields
      if (!attendee.organizationSize) {
        newErrors[`attendee-${index}-organizationSize`] =
          "Organization size is required";
        isValid = false;
        if (firstErrorAttendee === -1) {
          firstErrorAttendee = index;
          firstErrorField = "organizationSize";
        }
      }

      if (!attendee.industry) {
        newErrors[`attendee-${index}-industry`] = "Industry is required";
        isValid = false;
        if (firstErrorAttendee === -1) {
          firstErrorAttendee = index;
          firstErrorField = "industry";
        }
      }

      // Note: designation is now optional - no validation required
    });

    setFormErrors(newErrors);

    // Set a specific error message for the form indicating which attendee has issues
    if (!isValid && firstErrorAttendee !== -1) {
      const attendeeNumber = firstErrorAttendee + 1;
      const ticketType = attendees[firstErrorAttendee].ticketType || "attendee";
      const errorMessage = `Please complete all required fields for ${ticketType} #${attendeeNumber}.`;
      newErrors.form = errorMessage;
      setFormErrors(newErrors);
      setErrorSnackbarMessage(errorMessage);
      setShowErrorSnackbar(true);
    }

    return isValid;
  };

  // Update handleSubmit to use new attendees API
  const handleSubmit = async () => {
    if (!validateForm()) {
      // Scroll to the first error field more reliably
      setTimeout(() => {
        const firstErrorField = document.querySelector(
          '.Mui-error input, .error, [aria-invalid="true"]'
        );
        if (firstErrorField) {
          firstErrorField.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
          // Also try to focus the field
          firstErrorField.focus();
        } else {
          // If no specific field found, scroll to first form error
          const errorElement = document.querySelector(
            ".MuiFormHelperText-root, .selectErrorText"
          );
          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });
          }
        }
      }, 100);
      return;
    }

    setIsProcessing(true);

    try {
      if (!customerData || !customerData.customerId) {
        throw new Error("Customer information not found");
      }

      // Extract orderId from customerData (Razorpay order ID)
      const searchParams = new URLSearchParams(location?.search || "");
      const orderId = searchParams.get("orderId");

      if (!orderId) {
        throw new Error("Order ID not found");
      }

      // Format attendees data for API
      const attendeesData = attendees.map((attendee, index) => ({
        fullName: attendee.fullName,
        email: attendee.email,
        phone: attendee.phone, // Just the number without country code
        countryCallingCode: getSelectedCountry(index).dial_code,
        organizationName: attendee.organization,
        organizationSize: attendee.organizationSize,
        industry: attendee.industry,
        designation: attendee.designation || "",
        ticketTypeId: attendee.ticketId, // Map to specific ticket type
      }));

      const requestData = {
        orderId,
        attendees: attendeesData,
        customerId: customerData.customerId,
      };

      const response = await eventApiService.addAttendees(
        eventSlug,
        requestData
      );

      if (response.success) {
        setSubmitSuccess(true);

        // Refresh customer data to get updated attendee details with QR codes
        const searchParams = new URLSearchParams(location?.search || "");
        const customerId = searchParams.get("customerId");
        const orderId = searchParams.get("orderId");

        if (customerId && orderId) {
          await fetchCustomerDetails(customerId, orderId);
        }

        window.scrollTo(0, 0);
      } else {
        throw new Error(
          response.message || "Failed to update attendee information"
        );
      }
    } catch (error) {
      console.error("Error submitting attendee details:", error);
      setFormErrors((prev) => ({
        ...prev,
        form:
          error.message ||
          "An error occurred while submitting attendee details. Please try again.",
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    const fetchEventConfig = async () => {
      try {
        const response = await eventApiService.getEventConfig(eventSlug);
        if (response.success && response.data) {
          setEventConfig(response.data);
        }
      } catch (err) {
        console.error("Error fetching event config:", err);
      }
    };

    if (eventSlug) {
      fetchEventConfig();
    }
  }, [eventSlug]);

  // Effect to initialize component with URL parameters
  useEffect(() => {
    // Track payment completion
    posthog.capture("payment_completed", {
      event_slug: eventSlug,
      timestamp: new Date().toISOString(),
    });

    const searchParams = new URLSearchParams(location?.search || "");
    const customerId = searchParams.get("customerId");
    const orderId = searchParams.get("orderId");

    if (customerId && orderId) {
      fetchCustomerDetails(customerId, orderId);
    } else {
      setLoading(false);
      setErrorType("MISSING_PARAMETERS");
      setError("Customer ID or Order ID is missing from the URL");
    }
  }, [location, eventSlug]);

  // Add meta tag to prevent auto-zoom on iOS
  useEffect(() => {
    const metaTag = document.createElement("meta");
    metaTag.name = "viewport";
    metaTag.content =
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";
    document.getElementsByTagName("head")[0].appendChild(metaTag);

    return () => {
      document.getElementsByTagName("head")[0].removeChild(metaTag);
    };
  }, []);

  // Update document title dynamically
  useEffect(() => {
    if (eventData && eventData.eventName) {
      document.title = `Thank You - ${eventData.eventName}`;
    } else {
      document.title = "Thank You - Event Registration";
    }
  }, [eventData]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!preventExitConfirmation) {
        const message = "Are you sure you want to close this page? ";
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    const handlePopState = (event) => {
      if (!preventExitConfirmation) {
        const shouldLeave = window.confirm(
          "Are you sure you want to leave this page?"
        );
        if (!shouldLeave) {
          window.history.pushState(null, null, window.location?.pathname);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [preventExitConfirmation]);

  // Set up tracking scripts and mutation observer

  // Add accordion toggle function
  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <div className={classes.root}>
        <Container className={classes.container} maxWidth="lg">
          <Box className={classes.loadingContainer}>
            <CircularProgress style={{ color: primaryColor }} />
            <Typography className={classes.loadingText}>
              Loading your registration details...
            </Typography>
          </Box>
        </Container>
      </div>
    );
  }

  // Show error message based on error type
  if (error && errorType) {
    const getErrorMessage = () => {
      switch (errorType) {
        case "CUSTOMER_NOT_FOUND":
          return {
            title: "Registration Not Found",
            message:
              "We couldn't find your registration details. Please check your link or contact support.",
          };
        case "INVALID_EVENT":
          return {
            title: "Invalid Event",
            message: "This registration doesn't belong to the requested event.",
          };
        case "MISSING_PARAMETERS":
          return {
            title: "Incomplete Information",
            message:
              "Required information is missing from the URL. Please use the link from your confirmation email.",
          };
        case "INVALID_PARAMETER_TYPE":
          return {
            title: "Invalid Link",
            message:
              "The link format is incorrect. Please use the link from your confirmation email.",
          };
        case "PAYMENT_NOT_COMPLETED":
          return {
            title: "Payment Pending",
            message:
              "Your payment is not completed yet. Please complete the payment to access your registration details.",
          };
        default:
          return {
            title: "Something Went Wrong",
            message:
              "Unable to load your registration details. Please try again later.",
          };
      }
    };

    const errorInfo = getErrorMessage();

    return (
      <div className={classes.root}>
        <Container className={classes.container} maxWidth="lg">
          <Box className={classes.contentWrapper}>
            <Box className={classes.header}>
              <Box className={classes.logoContainer}>
                <img
                  // src={eventConfig?.brandLogo || AiSensyLogo}
                  src={AiSensyLogo}
                  alt="AiSensy Logo"
                  className={classes.logo}
                />
              </Box>
            </Box>

            {/* <Typography variant="h5" className={classes.eventTitle}>
              EVOLVE<span style={{ color: primaryColor }}>'25</span> : AI Edition
            </Typography> */}
            <Typography variant="h5" className={classes.eventTitle}>
              <span style={{ color: primaryColor }}>Ai</span>Persy: AI Hiring
              Assistant
            </Typography>

            {/* <Typography className={classes.subtitle}>
              {eventData?.location || "REGISTRATION SYSTEM"}
            </Typography> */}

            <Typography variant="body2" className={classes.eventDate}>
              <MapPinIcon className={classes.customIcon} />
              <span className={classes.eventDateText}>
                {eventData.location || "Location"}
              </span>
            </Typography>

            <Typography variant="body2" className={classes.eventDate}>
              <CalendarIcon className={classes.customIcon} />
              <span className={classes.eventDateText}>
                {eventData?.dateTime || "Date & Time"}
              </span>
            </Typography>

            <Box className={classes.thankYouSection}>
              <Typography
                variant="h5"
                style={{
                  color: "#ff6b6b",
                  fontWeight: 600,
                  marginBottom: "1rem",
                }}
              >
                {errorInfo.title}
              </Typography>
              <Typography variant="body1" style={{ marginBottom: "1.5rem" }}>
                {errorInfo.message}
              </Typography>
              <Button
                component={Link}
                to={`/${eventSlug}`}
                variant="contained"
                style={{
                  background: `${primaryColor}`,
                  color: "white",
                  fontWeight: 600,
                  padding: "0.7rem 2rem",
                  borderRadius: "8px",
                  textTransform: "none",
                }}
                onClick={() => setPreventExitConfirmation(true)}
              >
                Back to Registration
              </Button>
            </Box>
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Container className={classes.container} maxWidth="lg">
        <Box className={classes.contentWrapper}>
          <Box className={classes.header}>
            <Box className={classes.logoContainer}>
              <img
                // src={eventConfig?.brandLogo || AiSensyLogo}
                src={AiSensyLogo}
                alt="AiSensy Logo"
                className={classes.logo}
              />
            </Box>
          </Box>

          <Typography variant="h5" className={classes.eventTitle}>
            <span style={{ color: primaryColor }}>Ai</span>Persy: AI Hiring
            Assistant
          </Typography>

          {/* <Typography className={classes.subtitle}>
            {eventData?.location?.toUpperCase() || "REGISTRATION SYSTEM"}
          </Typography> */}

          {/* Event badges/chips - Dynamic based on event data */}
          {/* <Box className={classes.eventBadges}>
            <Chip
              icon={
                <MapPinIcon
                  className={`${classes.customIcon} ${classes.dubaiChipIcon}`}
                  style={{ marginRight: "-4px" }}
                />
              }
              label={eventData?.location || "Location"}
              className={classes.dubaiChip}
              size="small"
            />
            <Chip
              icon={
                <MessageSquareIcon
                  className={`${classes.customIcon} ${classes.whatsappChipIcon}`}
                  style={{ marginRight: "-4px" }}
                />
              }
              label="WhatsApp"
              className={classes.whatsappChip}
              size="small"
            />
            <Chip
              icon={
                <GlobeIcon
                  className={`${classes.customIcon} ${classes.aiChipIcon}`}
                  style={{ marginRight: "-4px" }}
                />
              }
              label="AI"
              className={classes.aiChip}
              size="small"
            />
            <Chip
              icon={
                <BriefcaseIcon
                  className={`${classes.customIcon} ${classes.businessChipIcon}`}
                  style={{ marginRight: "-4px" }}
                />
              }
              label="Business"
              className={classes.businessChip}
              size="small"
            />
          </Box> */}

          {/* Dynamic event details */}
          {/* {customerData && (
            <Box className={classes.eventTimeLocation}>
              <Box className={classes.eventDetailPill}>
                <EventIcon className={classes.eventDetailIcon} />
                <span>{customerData.eventName}</span>
              </Box>
              <Box className={classes.eventDetailPill}>
                <PlaceIcon className={classes.eventDetailIcon} />
                <span>{customerData.location}</span>
              </Box>
            </Box>
          )} */}

          {/* Main content based on customer journey state */}
          {submitSuccess && attendees && attendees.length > 0 ? (
            // Show completion with attendee list and QR codes
            <>
              <Box className={classes.thankYouSection}>
                <Box className={classes.successIcon}>
                  <CheckCircleIcon />
                </Box>
                <Typography className={classes.congratsText}>
                  Registration Complete!
                </Typography>
                <Typography variant="body1" className={classes.messageText}>
                  You're all set for{" "}
                  <span className={classes.highlightText}>
                    {eventData?.eventName || "this event"}
                  </span>
                  . Your tickets and event details have been sent to your email
                  and WhatsApp.
                </Typography>
              </Box>

              {/* Enhanced Order Breakdown in Accordion */}
              <Accordion
                defaultExpanded={false}
                className={classes.orderAccordion}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={classes.orderAccordionSummary}
                >
                  <Box className={classes.accordionHeader}>
                    <ReceiptIcon className={classes.accordionIcon} />
                    <Typography className={classes.accordionTitle}>
                      Order Summary
                    </Typography>
                    <Box className={classes.paymentStatusSmall}>
                      <CheckCircleIcon />
                      Successful
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.orderAccordionDetails}>
                  <OrderBreakdown
                    customerData={customerData}
                    eventData={eventData}
                    classes={classes}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Show attendee details with QR codes */}
              <Box className={classes.orderSummarySection}>
                <Typography className={classes.sectionTitle}>
                  Attendee Details & QR Codes
                </Typography>

                {attendees.map((attendee, index) => (
                  <Box
                    key={attendee.id || index}
                    className={classes.attendeeForm}
                  >
                    <Box className={classes.formHeader}>
                      <Typography className={classes.attendeeTitle}>
                        <span className={classes.attendeeCount}>
                          {index + 1}
                        </span>
                        {attendee.ticketTitle || attendee.ticketType}
                      </Typography>

                      {attendee?.qrCodeUrl && (
                        <Button
                          component="a"
                          href={`${attendee.qrCodeUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          className={classes.copyButton}
                          style={{ textTransform: "none" }}
                        >
                          Download QR
                        </Button>
                      )}
                    </Box>

                    <Box className={classes.formContent}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Full Name:</strong>
                          </Typography>
                          <Typography variant="body1">
                            {attendee.fullName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Email:</strong>
                          </Typography>
                          <Typography variant="body1">
                            {attendee.email}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Phone:</strong>
                          </Typography>
                          <Typography variant="body1">
                            {attendee.phone}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Ticket ID:</strong>
                          </Typography>
                          <Typography
                            variant="body1"
                            style={{ fontFamily: "monospace" }}
                          >
                            {attendee.ticketId}
                          </Typography>
                        </Grid>
                        {attendee?.qrCodeUrl && (
                          <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" mt={2}>
                              <img
                                src={attendee.qrCodeUrl}
                                alt={`QR Code for ${attendee.fullName}`}
                                style={{ width: 150, height: 150 }}
                              />
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Box>
                ))}

                {/* <Box className={classes.formSubmitArea}>
                  <Button
                    component={Link}
                    to={`/${eventSlug}`}
                    variant="contained"
                    className={classes.bookAgainButton}
                  >
                    Register Another Person
                  </Button>
                </Box> */}
              </Box>
            </>
          ) : customerData?.needsAttendeeDetails ? (
            // Show attendee forms for multiple attendees
            <>
              <Box className={classes.thankYouSection}>
                <Typography className={classes.congratsText}>
                  Thank You for Your Purchase!
                </Typography>
                <Typography variant="body1" className={classes.messageText}>
                  You're almost there! Your tickets for{" "}
                  <span className={classes.highlightText}>
                    {eventData?.eventName || "this event"}
                  </span>{" "}
                  are reserved. Just complete the attendee details below to lock
                  in your spot—make sure everything's accurate so your passes
                  land safely in your inbox!
                </Typography>
              </Box>

              {/* Enhanced Order Breakdown in Accordion */}
              <Accordion
                defaultExpanded={false}
                className={classes.orderAccordion}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={classes.orderAccordionSummary}
                >
                  <Box className={classes.accordionHeader}>
                    <ReceiptIcon className={classes.accordionIcon} />
                    <Typography className={classes.accordionTitle}>
                      Order Summary
                    </Typography>
                    <Box className={classes.paymentStatusSmall}>
                      <CheckCircleIcon />
                      Successful
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.orderAccordionDetails}>
                  <OrderBreakdown
                    customerData={customerData}
                    eventData={eventData}
                    classes={classes}
                  />
                </AccordionDetails>
              </Accordion>

              {/* Attendee forms */}
              <Box className={classes.formSection}>
                <Typography className={classes.formTitle}>
                  Complete Attendee Information
                </Typography>

                {attendees.map((attendee, index) => (
                  <Box key={index} className={classes.attendeeForm}>
                    <Box className={classes.formHeader}>
                      <Typography className={classes.attendeeTitle}>
                        <span className={classes.attendeeCount}>
                          {index + 1}
                        </span>
                        {attendee.ticketType}
                      </Typography>

                      {index > 0 && (
                        <Button
                          startIcon={
                            <ContentCopyIcon className={classes.copyIcon} />
                          }
                          size="small"
                          onClick={() => copyFromFirstAttendee(index)}
                          className={classes.copyButton}
                        >
                          Same as First
                        </Button>
                      )}
                    </Box>

                    <Box className={classes.formContent}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            className={classes.formFieldLabel}
                          >
                            Full Name*
                          </Typography>
                          <TextField
                            className={classes.formFieldInput}
                            variant="outlined"
                            fullWidth
                            placeholder="Enter full name"
                            value={attendee.fullName || ""}
                            onChange={(e) =>
                              handleAttendeeChange(
                                index,
                                "fullName",
                                e.target.value
                              )
                            }
                            error={!!formErrors[`attendee-${index}-fullName`]}
                            helperText={
                              formErrors[`attendee-${index}-fullName`] || ""
                            }
                            InputLabelProps={{ shrink: false }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            className={classes.formFieldLabel}
                          >
                            Email Address*
                          </Typography>
                          <TextField
                            className={classes.formFieldInput}
                            variant="outlined"
                            type="email"
                            fullWidth
                            placeholder="Enter email address"
                            value={attendee.email || ""}
                            onChange={(e) =>
                              handleAttendeeChange(
                                index,
                                "email",
                                e.target.value
                              )
                            }
                            error={!!formErrors[`attendee-${index}-email`]}
                            helperText={
                              formErrors[`attendee-${index}-email`] || ""
                            }
                            InputLabelProps={{ shrink: false }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            className={classes.formFieldLabel}
                          >
                            Phone Number*
                          </Typography>
                          <Box className={classes.phoneInputContainer}>
                            <TextField
                              fullWidth
                              variant="outlined"
                              placeholder="Enter your phone number"
                              value={attendee.phone || ""}
                              onChange={(e) => {
                                const numericValue = e.target.value.replace(
                                  /[^0-9]/g,
                                  ""
                                );
                                handleAttendeeChange(
                                  index,
                                  "phone",
                                  numericValue
                                );
                              }}
                              className={classes.formFieldInput}
                              error={!!formErrors[`attendee-${index}-phone`]}
                              helperText={
                                formErrors[`attendee-${index}-phone`] || ""
                              }
                              InputLabelProps={{ shrink: false }}
                              type="tel"
                              inputProps={{
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment
                                    position="start"
                                    style={{ marginRight: 0 }}
                                  >
                                    <Box
                                      className={classes.countryCodeInput}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <Select
                                        value={getSelectedCountry(index).code}
                                        onChange={(e) => {
                                          const country = countries.find(
                                            (c) => c.code === e.target.value
                                          );
                                          if (country) {
                                            updateSelectedCountry(
                                              index,
                                              country
                                            );
                                          }
                                        }}
                                        displayEmpty
                                        renderValue={() => (
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                          >
                                            <span
                                              style={{
                                                marginRight: 4,
                                                fontSize: 16,
                                              }}
                                            >
                                              {getSelectedCountry(index).flag}
                                            </span>
                                            <span
                                              style={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                color: "#333",
                                              }}
                                            >
                                              {
                                                getSelectedCountry(index)
                                                  .dial_code
                                              }
                                            </span>
                                          </Box>
                                        )}
                                        MenuProps={{
                                          anchorOrigin: {
                                            vertical: "bottom",
                                            horizontal: "left",
                                          },
                                          transformOrigin: {
                                            vertical: "top",
                                            horizontal: "left",
                                          },
                                          getContentAnchorEl: null,
                                          PaperProps: {
                                            className: classes.mobileMenuPaper,
                                          },
                                          disableRestoreFocus: true,
                                          autoFocus: false,
                                        }}
                                        style={{
                                          width: "auto",
                                          height: "100%",
                                          padding: "0",
                                          border: "none",
                                          boxShadow: "none",
                                        }}
                                        disableUnderline={true}
                                      >
                                        <MenuItem
                                          disableRipple
                                          disableTouchRipple
                                          style={{
                                            position: "sticky",
                                            top: 0,
                                            zIndex: 10,
                                            backgroundColor: "white",
                                            opacity: 1,
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          onMouseDown={(e) =>
                                            e.stopPropagation()
                                          }
                                        >
                                          <TextField
                                            fullWidth
                                            placeholder="Search country..."
                                            value={countrySearch}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              setCountrySearch(e.target.value);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            variant="standard"
                                            size="small"
                                            InputProps={{
                                              startAdornment: (
                                                <InputAdornment position="start">
                                                  <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                              ),
                                            }}
                                            style={{
                                              borderRadius: "6px",
                                              padding: "4px 12px",
                                            }}
                                          />
                                        </MenuItem>
                                        {getFilteredCountries().length > 0 ? (
                                          getFilteredCountries().map(
                                            (country) => (
                                              <MenuItem
                                                key={country.code}
                                                value={country.code}
                                                style={{ minHeight: "40px" }}
                                              >
                                                <Box
                                                  display="flex"
                                                  alignItems="center"
                                                >
                                                  <span
                                                    style={{
                                                      marginRight: 8,
                                                      fontSize: 16,
                                                    }}
                                                  >
                                                    {country.flag}
                                                  </span>
                                                  <span
                                                    style={{ fontSize: 14 }}
                                                  >
                                                    {country.name}
                                                  </span>
                                                  <span
                                                    style={{
                                                      marginLeft: 8,
                                                      color: "#666",
                                                      fontSize: 14,
                                                    }}
                                                  >
                                                    {country.dial_code}
                                                  </span>
                                                </Box>
                                              </MenuItem>
                                            )
                                          )
                                        ) : (
                                          <MenuItem disabled>
                                            No countries found
                                          </MenuItem>
                                        )}
                                      </Select>
                                    </Box>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            className={classes.formFieldLabel}
                          >
                            Organization*
                          </Typography>
                          <TextField
                            className={classes.formFieldInput}
                            variant="outlined"
                            fullWidth
                            placeholder="Enter organization name"
                            value={attendee.organization || ""}
                            onChange={(e) =>
                              handleAttendeeChange(
                                index,
                                "organization",
                                e.target.value
                              )
                            }
                            error={
                              !!formErrors[`attendee-${index}-organization`]
                            }
                            helperText={
                              formErrors[`attendee-${index}-organization`] || ""
                            }
                            InputLabelProps={{ shrink: false }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            className={classes.formFieldLabel}
                          >
                            Designation
                          </Typography>
                          <TextField
                            className={classes.formFieldInput}
                            variant="outlined"
                            fullWidth
                            placeholder="Enter designation (optional)"
                            value={attendee.designation || ""}
                            onChange={(e) =>
                              handleAttendeeChange(
                                index,
                                "designation",
                                e.target.value
                              )
                            }
                            InputLabelProps={{ shrink: false }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            className={classes.formFieldLabel}
                          >
                            Website
                          </Typography>
                          <TextField
                            className={classes.formFieldInput}
                            variant="outlined"
                            fullWidth
                            placeholder="Enter website URL (optional)"
                            value={attendee.website || ""}
                            onChange={(e) =>
                              handleAttendeeChange(
                                index,
                                "website",
                                e.target.value
                              )
                            }
                            InputLabelProps={{ shrink: false }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            className={classes.formFieldLabel}
                          >
                            Industry*
                          </Typography>
                          <select
                            className={`${classes.simpleSelect} ${
                              formErrors[`attendee-${index}-industry`]
                                ? "error"
                                : ""
                            }`}
                            value={attendee.industry || ""}
                            onChange={(e) =>
                              handleAttendeeChange(
                                index,
                                "industry",
                                e.target.value
                              )
                            }
                          >
                            <option value="" disabled>
                              Select your industry
                            </option>
                            {industries.map((industry) => (
                              <option key={industry} value={industry}>
                                {industry}
                              </option>
                            ))}
                          </select>
                          {formErrors[`attendee-${index}-industry`] && (
                            <div className={classes.selectErrorText}>
                              {formErrors[`attendee-${index}-industry`]}
                            </div>
                          )}
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="body2"
                            className={classes.formFieldLabel}
                          >
                            Organization Size*
                          </Typography>
                          <select
                            className={`${classes.simpleSelect} ${
                              formErrors[`attendee-${index}-organizationSize`]
                                ? "error"
                                : ""
                            }`}
                            value={attendee.organizationSize || ""}
                            onChange={(e) =>
                              handleAttendeeChange(
                                index,
                                "organizationSize",
                                e.target.value
                              )
                            }
                          >
                            <option value="" disabled>
                              Select organization size
                            </option>
                            {organizationSizes.map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                          {formErrors[`attendee-${index}-organizationSize`] && (
                            <div className={classes.selectErrorText}>
                              {formErrors[`attendee-${index}-organizationSize`]}
                            </div>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                ))}

                <Box className={classes.formSubmitArea}>
                  <Button
                    color="primary"
                    size="large"
                    disabled={isProcessing}
                    onClick={handleSubmit}
                    className={classes.submitButton}
                    type="submit"
                  >
                    {isProcessing ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Submit Attendee Details"
                    )}
                  </Button>

                  {formErrors.form && (
                    <Typography
                      style={{
                        color: "#f44336",
                        marginTop: "8px",
                        fontSize: "0.75rem",
                        textAlign: "center",
                        fontWeight: 500,
                      }}
                    >
                      {formErrors.form}
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          ) : (
            // Single attendee or completed registration - show success
            <Box className={classes.thankYouSection}>
              <Box className={classes.successIcon}>
                <CheckCircleIcon />
              </Box>
              <Typography className={classes.congratsText}>
                Registration Complete!
              </Typography>
              <Typography variant="body1" className={classes.messageText}>
                You're all set for{" "}
                <span className={classes.highlightText}>
                  {eventData?.eventName || "this event"}
                </span>
                ! Your tickets and QR codes have been sent to your email and
                WhatsApp.
              </Typography>

              {/* Enhanced Order Breakdown in Accordion for single attendee */}
              <Accordion
                defaultExpanded={false}
                className={classes.orderAccordion}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={classes.orderAccordionSummary}
                >
                  <Box className={classes.accordionHeader}>
                    <ReceiptIcon className={classes.accordionIcon} />
                    <Typography className={classes.accordionTitle}>
                      Order Summary
                    </Typography>
                    <Box className={classes.paymentStatusSmall}>
                      <CheckCircleIcon />
                      Successful
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.orderAccordionDetails}>
                  <OrderBreakdown
                    customerData={customerData}
                    eventData={eventData}
                    classes={classes}
                  />
                </AccordionDetails>
              </Accordion>

              <Button
                component={Link}
                to={`/${eventSlug}`}
                variant="contained"
                className={classes.bookAgainButton}
                style={{ marginTop: "1.5rem" }}
                onClick={() => setPreventExitConfirmation(true)}
              >
                Register Another Person
              </Button>
            </Box>
          )}
        </Box>
      </Container>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        style={{
          bottom: 24,
        }}
      >
        <div
          style={{
            backgroundColor: "#ff6b6b",
            color: "white",
            padding: "12px 20px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
            boxShadow: "0 4px 12px rgba(255, 107, 107, 0.3)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
          onClick={() => setShowErrorSnackbar(false)}
        >
          ⚠️ {errorSnackbarMessage}
        </div>
      </Snackbar>
    </div>
  );
}

export default ThankYouPage;
